import { useTranslations } from 'next-intl';
import { type SetStateAction, useCallback, useEffect, useState } from 'react';
import { toast } from '@/app/components/ui/use-toast';
import { MAX_AUDIO_REFERENCE_PROMPTS, MENTION_COLOR_MAP } from '@/app/constants';
import { useStore } from '@/app/providers/store';
import type {
  AudioUploadFile,
  BeforeAddMention,
  ElementInfo,
  FormTabType,
  ImagePrompt,
  ImageUploadFile,
  ReferencePrompt,
  SourceAudio,
  TextPromptWithInputItem,
  ToolFormTabType,
  UploadImageType,
  VideoPrompt,
  VideoUploadFile,
} from '@/app/types';
import {
  findVoiceReference,
  getAudioReferenceCount,
  getDefaultRecaptionPrompt,
  getImageAndElementCount,
  getMaxImageAndElementCount,
  getMaxVideoCount,
  getMentionColor,
  getNextAudioName,
  getNextAvailableAudioIdx,
  getNextAvailableImageIdx,
  getNextAvailableVideoIdx,
  getNextImageName,
  getNextVideoName,
  getPromptMaxLength,
  getTextPromptLength,
  getVideoAndVideoElementCount,
  reportEvent,
} from '@/app/utils';
import { getBackfillImageId } from '@/app/utils/client';
import { useLocale, usePathname } from '@/i18n/routing';
import { useGlobalModal } from './use-global-modal';
import { useMentionablePromptsData } from './use-mentionable-prompts-data';

export type AddReferenceAndMentionsOptions = {
  extraReferences?: ReferencePrompt[];
  appendItems?: (reference: ReferencePrompt, extraReferences: ReferencePrompt[]) => TextPromptWithInputItem;
};

// 辅助函数：revoke 图片/视频的 blob URL
const revokeBlobUrls = (reference: ReferencePrompt) => {
  if (!reference.prompt) return;

  // 处理图片类型的 blob URL
  if (reference.type === 'image') {
    const imagePrompt = reference.prompt as ImagePrompt;
    if (imagePrompt.sourceImagePreview?.startsWith('blob:')) {
      URL.revokeObjectURL(imagePrompt.sourceImagePreview);
    }
    if (imagePrompt.selectedImagePreview?.startsWith('blob:')) {
      URL.revokeObjectURL(imagePrompt.selectedImagePreview);
    }
  }

  // 处理视频类型的 blob URL
  if (reference.type === 'video') {
    const videoPrompt = reference.prompt as VideoPrompt;
    if (videoPrompt.videoPreview?.startsWith('blob:')) {
      URL.revokeObjectURL(videoPrompt.videoPreview);
    }
    if (videoPrompt.coverImage?.startsWith('blob:')) {
      URL.revokeObjectURL(videoPrompt.coverImage);
    }
    if (videoPrompt.coverImagePreview?.startsWith('blob:')) {
      URL.revokeObjectURL(videoPrompt.coverImagePreview);
    }
  }

  if (reference.type === 'audio') {
    const audioPrompt = reference.prompt;
    if (audioPrompt?.preview?.startsWith('blob:')) {
      URL.revokeObjectURL(audioPrompt.preview);
    }
  }
};

const toVideoPrompt = (file: NonNullable<VideoUploadFile['file']>): VideoPrompt => ({
  video: file.src || file.preview,
  videoPreview: file.preview,
  duration: file.duration,
  coverImage: file.cover_url,
  coverImagePreview: file.cover_preview_url,
  width: file.width,
  height: file.height,
  aspectRatio: file.aspectRatio,
  videoId: file.id,
  _tempFile: file._tempFile,
  _tempCoverImage: file._tempCoverImage,
});

const getReferencePreview = (
  type: ReferencePrompt['type'],
  prompt: ReferencePrompt['prompt'] | null | undefined,
): string | undefined => {
  if (!prompt) return undefined;
  if (type === 'image') {
    const imagePrompt = prompt as ImagePrompt;
    return (
      imagePrompt.selectedImagePreview ||
      imagePrompt.sourceImagePreview ||
      imagePrompt.selectedImage ||
      imagePrompt.sourceImage
    );
  }
  if (type === 'video') {
    const videoPrompt = prompt as VideoPrompt;
    return videoPrompt.coverImagePreview || videoPrompt.coverImage;
  }
  // Audio references use an icon-only mention. Audio URLs are playable sources,
  // not visual thumbnails, so never copy them into mention.preview.
  if (type === 'audio') return undefined;

  const component = (prompt as ElementInfo).components?.[0];
  return component?.cover_uri || component?.content;
};

export function useMentionablePrompts(
  taskType: Exclude<FormTabType | ToolFormTabType, 'scene'>,
  options?: { onReferencePromptsChange?: () => void; allowTempMode?: boolean },
) {
  const { formParams, updateFormParams, getFormParams } = useMentionablePromptsData(taskType);
  const { onReferencePromptsChange, allowTempMode = true } = options || {};
  const locale = useLocale();
  const pathname = usePathname();
  const t = useTranslations();
  const transProMode = useTranslations('create.proMode');
  const { trialsProMode, isFreeUser, setFormParams, userInfo } = useStore((state) => ({
    trialsProMode: state.trialsProMode,
    isFreeUser: state.userInfo?.subs_plan === 'free',
    setFormParams: state.setFormParams,
    userInfo: state.userInfo,
  }));
  const { open: openLoginDialog } = useGlobalModal('loginDialog');
  const { open: openConfirmDialog } = useGlobalModal('confirmDialog');
  const canUseProMode = !isFreeUser || !!trialsProMode?.remaining;

  const { textPromptWithReference: textPrompt = [], referencePrompts = [], isProMode } = formParams;
  const promptMaxLength = getPromptMaxLength(formParams.modelVersion);
  const [showMaxReferenceError, setShowMaxReferenceError] = useState<boolean>(false);

  // 把被删除的参考缓存在回收站，用于从剪切和粘贴中恢复
  // 增加灵感参数信息，用于撤销时恢复
  const [deletedReferences, setDeletedReferences] = useState<
    Record<
      string,
      ReferencePrompt & {
        inspirationParams?: { inspirationId?: string; inspirationFrom?: string; inspirationImageUrl?: string };
        associatedReferences?: ReferencePrompt[];
      }
    >
  >({});

  // 从回收站中移除
  const removeReferenceFromTrash = (id: string, isRevokeBlobUrls = true) => {
    setDeletedReferences((prev) => {
      const referenceToRemove = prev[id];
      if (isRevokeBlobUrls && referenceToRemove) {
        // 在删除前 revoke blob URL
        revokeBlobUrls(referenceToRemove);
      }
      const newDeletedReferences = { ...prev };
      delete newDeletedReferences[id];
      return newDeletedReferences;
    });
  };

  // 组件卸载时清理回收站中的所有 blob URL（组件卸载时 React 会自动清理回收站，但需要手动清理 blob URL）
  useEffect(() => {
    return () => {
      Object.values(deletedReferences).forEach((reference) => {
        revokeBlobUrls(reference);
      });
    };
  }, []); // 只在组件卸载时执行

  const setReferencePrompts = (updater: SetStateAction<ReferencePrompt[]>) => {
    if (Array.isArray(updater)) {
      updateFormParams('referencePrompts', updater);
    } else {
      const prevState = getFormParams().referencePrompts || [];
      updateFormParams('referencePrompts', updater(prevState));
    }
  };

  const setTextPrompt = (updater: SetStateAction<TextPromptWithInputItem>) => {
    if (Array.isArray(updater)) {
      updateFormParams('textPromptWithReference', updater);
    } else {
      updateFormParams('textPromptWithReference', updater(getFormParams().textPromptWithReference || []));
    }
  };

  // 更新 recaptionPrompt 的提及（同时处理 subject 和 environment）
  const updateRecaptionPrompt = (
    updater: (prev: TextPromptWithInputItem) => TextPromptWithInputItem,
    field: 'subject' | 'environment' | 'both' = 'subject',
  ) => {
    const prevState = getFormParams().recaptionPrompt || getDefaultRecaptionPrompt(transProMode);

    if (field === 'both') {
      // 同时更新 subject 和 environment
      const updatedSubject = updater(prevState.subject || []);
      const updatedEnvironment = updater(prevState.environment || []);

      updateFormParams('recaptionPrompt', {
        ...prevState,
        subject: updatedSubject,
        environment: updatedEnvironment,
      });
    } else {
      updateFormParams('recaptionPrompt', {
        ...prevState,
        [field]: updater(prevState[field] || []),
      });
    }
  };

  const getDefaultMentionItems = (reference: ReferencePrompt): TextPromptWithInputItem => [
    {
      type: 'mention',
      mention: {
        id: reference.id,
        name: reference.name,
        color: getMentionColor({ element: reference.prompt as ElementInfo, type: reference.type }),
        ...(getReferencePreview(reference.type, reference.prompt)
          ? { preview: getReferencePreview(reference.type, reference.prompt) }
          : {}),
        ...(reference.type !== 'element' ? { mediaType: reference.type } : {}),
      },
    },
  ];

  // 检查 textPrompt 是否已达到最大长度限制，如果达到，则弹出提示
  const checkAndToastTextPromptLength = (appendItems: TextPromptWithInputItem) => {
    const contentLength = getTextPromptLength(getFormParams().textPromptWithReference || []); // 检查 textPrompt 是否已达到最大长度限制
    if (contentLength + getTextPromptLength(appendItems) >= promptMaxLength) {
      toast({
        type: 'warning',
        message: t('create.createForm.error.invalidLengthWithMention', { limit: promptMaxLength }),
      });
      return false;
    }
    return true;
  };

  const addReferenceAndMentions = (reference: ReferencePrompt, options?: AddReferenceAndMentionsOptions) => {
    const extraReferences = options?.extraReferences ?? [];
    const appendItems = options?.appendItems?.(reference, extraReferences) ?? getDefaultMentionItems(reference);

    if (!checkAndToastTextPromptLength(appendItems)) return false;

    const currentForm = getFormParams();
    const nextReferences = [...(currentForm.referencePrompts ?? []), reference, ...extraReferences];
    if (isProMode && canUseProMode) {
      const recaption = currentForm.recaptionPrompt ?? getDefaultRecaptionPrompt(transProMode);
      setFormParams(taskType, {
        referencePrompts: nextReferences,
        recaptionPrompt: { ...recaption, subject: [...recaption.subject, ...appendItems] },
      });
    } else {
      setFormParams(taskType, {
        referencePrompts: nextReferences,
        textPromptWithReference: [...(currentForm.textPromptWithReference ?? []), ...appendItems],
      });
    }
    // 如果是 uploading，则不触发回调
    if (reference.status !== 'uploading' || extraReferences.some((ref) => ref.status !== 'uploading')) {
      onReferencePromptsChange?.();
    }

    return true;
  };

  const removeReferenceAndMentions = (idToRemove: string, isRecoverable = true) => {
    const referenceToRemove = getFormParams().referencePrompts?.find((prompt) => prompt.id === idToRemove);
    if (!referenceToRemove) return;

    const currentFormParams = getFormParams();
    const inspirationImageUrl = currentFormParams.inspirationImageUrl;
    let savedInspirationParams:
      | { inspirationId?: string; inspirationFrom?: string; inspirationImageUrl?: string }
      | undefined;

    // 如果删除的参考图是 Apply 的灵感图，保存并清空提交任务的灵感参数
    if (
      referenceToRemove.prompt &&
      'sourceImage' in referenceToRemove.prompt &&
      referenceToRemove.prompt.sourceImage === inspirationImageUrl
    ) {
      // 保存当前的灵感参数，用于撤销时恢复
      savedInspirationParams = {
        inspirationId: currentFormParams.inspirationId,
        inspirationFrom: currentFormParams.inspirationFrom,
        inspirationImageUrl: currentFormParams.inspirationImageUrl,
      };

      setFormParams(taskType, {
        inspirationId: undefined,
        inspirationFrom: undefined,
        inspirationImageUrl: undefined,
      });
    }

    const subjectVoiceId =
      referenceToRemove.type === 'element' && referenceToRemove.useElementVoice
        ? referenceToRemove.prompt?.voice?.id
        : undefined;
    const associatedReferences = subjectVoiceId
      ? currentFormParams.referencePrompts?.filter(
          (reference) => reference.type === 'audio' && reference.prompt?.originElementVoiceId === subjectVoiceId,
        )
      : undefined;
    // 添加到回收站
    if (isRecoverable) {
      setDeletedReferences((prev) => ({
        ...prev,
        [idToRemove]: {
          ...referenceToRemove,
          ...(associatedReferences?.length ? { associatedReferences } : {}),
          ...(savedInspirationParams && { inspirationParams: savedInspirationParams }),
        },
      }));
    } else {
      // 如果不放入回收站（直接删除），立即 revoke blob URL
      revokeBlobUrls(referenceToRemove);
    }

    const removedIds = new Set([idToRemove]);
    if (referenceToRemove.type === 'element' && referenceToRemove.prompt?.voice?.id) {
      const voiceId = referenceToRemove.prompt.voice.id;
      const stillUsed = currentFormParams.referencePrompts?.some(
        (reference) =>
          reference.id !== idToRemove &&
          reference.type === 'element' &&
          reference.useElementVoice &&
          reference.prompt?.voice?.id === voiceId,
      );
      if (!stillUsed) {
        for (const reference of currentFormParams.referencePrompts ?? []) {
          if (
            reference.type === 'audio' &&
            reference.prompt?.originElementId &&
            reference.prompt.originElementVoiceId === voiceId
          )
            removedIds.add(reference.id);
        }
      }
    }
    const removedVoiceId =
      referenceToRemove.type === 'audio' ? referenceToRemove.prompt?.originElementVoiceId : undefined;
    // Keep a shared voice until its last subject is removed.
    setReferencePrompts((prev) =>
      prev
        .filter((reference) => !removedIds.has(reference.id))
        .map((reference) =>
          removedVoiceId && reference.type === 'element' && reference.prompt?.voice?.id === removedVoiceId
            ? { ...reference, useElementVoice: false }
            : reference,
        ),
    );
    onReferencePromptsChange?.();

    // 移除 @
    const updater = (prev: TextPromptWithInputItem): TextPromptWithInputItem =>
      prev.filter((item) => !(item.type === 'mention' && removedIds.has(item.mention.id)));
    if (isProMode && canUseProMode) {
      updateRecaptionPrompt(updater, 'both');
    } else {
      setTextPrompt(updater);
    }
  };

  const handleBeforeDeleteMention = useCallback(
    (mentionsIdsToDelete: string[], updatedContent: TextPromptWithInputItem) => {
      const referenceIdsToDelete = mentionsIdsToDelete.filter((mentionId) => {
        const mentionExist = updatedContent.find((item) => item.type === 'mention' && item.mention.id === mentionId);
        return !mentionExist;
      });

      if (referenceIdsToDelete.length === 0) {
        return true;
      }

      openConfirmDialog({
        content: t('create.char2VideoForm.dialog.deleteConfirm.content'),
        secondaryButtonText: t('create.char2VideoForm.dialog.deleteConfirm.closeButtonText'),
        primaryButtonText: t('create.char2VideoForm.dialog.deleteConfirm.confirmButtonText'),
        onPrimaryAction: () => {
          if (isProMode && canUseProMode) {
            updateRecaptionPrompt(() => updatedContent, 'both');
          } else {
            setTextPrompt(updatedContent);
          }
          referenceIdsToDelete.forEach((mentionId) => {
            removeReferenceAndMentions(mentionId);
          });
        },
      });

      return false;
    },
    [canUseProMode, isProMode, openConfirmDialog, removeReferenceAndMentions, t, updateRecaptionPrompt],
  );

  const updateReferenceAndMentions = (
    targetId: string,
    newReference: Partial<ReferencePrompt>,
    shouldTriggerCallback = true,
  ) => {
    const existingReference = getFormParams().referencePrompts?.find((p) => p.id === targetId);
    const type = newReference.type || existingReference?.type;

    const previousVoice =
      existingReference?.type === 'audio' ? existingReference.prompt?.originElementVoiceId : undefined;
    const nextVoice =
      newReference.prompt && 'originElementVoiceId' in newReference.prompt
        ? newReference.prompt.originElementVoiceId
        : undefined;
    // Uploading keeps the original prompt so failed replacements retain their bindings.
    const detachVoice = previousVoice && 'prompt' in newReference && nextVoice !== previousVoice;
    setReferencePrompts((prev) =>
      prev.map((prompt) => {
        if (prompt.id === targetId) return { ...prompt, ...newReference } as ReferencePrompt;
        if (detachVoice && prompt.type === 'element' && prompt.prompt?.voice?.id === previousVoice) {
          return { ...prompt, useElementVoice: false };
        }
        return prompt;
      }),
    );

    if (shouldTriggerCallback) {
      onReferencePromptsChange?.();
    }

    const updater = (prev: TextPromptWithInputItem): TextPromptWithInputItem =>
      prev.map((item) => {
        if (item.type === 'mention' && item.mention.id === targetId) {
          const preview = type ? getReferencePreview(type, newReference.prompt) : undefined;

          return {
            ...item,
            mention: {
              id: newReference.id || item.mention.id,
              name: newReference.name || item.mention.name,
              color: getMentionColor({
                element: newReference.prompt as ElementInfo,
                type: type,
                color: item.mention.color,
              }),
              ...(preview !== undefined ? { preview } : {}),
              ...(type && type !== 'element' ? { mediaType: type } : {}),
            },
          };
        }
        return item;
      });
    if (isProMode && canUseProMode) {
      updateRecaptionPrompt(updater, 'both');
    } else {
      setTextPrompt(updater);
    }
  };

  // 从回收站中移除图片
  const removeImageInTrash = (nextImageName: string) => {
    const imageInTrash = Object.values(deletedReferences).find((ref) => ref.name === nextImageName);
    if (imageInTrash) {
      removeReferenceFromTrash(imageInTrash.id);
    }
  };

  const handleUploadImage = (
    uploadFile: ImageUploadFile,
    onUploadStatusChange?: (status: ImageUploadFile['status']) => void,
  ) => {
    if (uploadFile.status === 'uploading') {
      if (getFormParams().referencePrompts?.some((prompt) => prompt.id === uploadFile.id)) {
        if (uploadFile.file) {
          updateReferenceAndMentions(uploadFile.id, { prompt: uploadFile.file }, false);
        }
        return;
      }

      // 如果未登录且不允许临时模式，弹出登录弹窗并拦截
      if (!userInfo && !allowTempMode) {
        openLoginDialog();
        reportEvent('login_pop_exposure', {
          refer_element: 'upload-image',
          refer_page: pathname,
        });
        return;
      }

      // 步骤1：立即生成名称
      const existedReferencePrompts = getFormParams().referencePrompts || [];
      const nextNameIdx = getNextAvailableImageIdx(existedReferencePrompts, locale);
      const nextImageName = getNextImageName(nextNameIdx, locale);

      // 步骤2：检查回收站冲突并清理
      removeImageInTrash(nextImageName);

      // 步骤3：添加到参考列表和文案中
      addReferenceAndMentions({
        type: 'image',
        status: 'uploading',
        prompt: uploadFile.file,
        id: uploadFile.id,
        name: nextImageName,
      });
      return;
    }
    onUploadStatusChange?.(uploadFile.status);
    if (uploadFile.status === 'done') {
      // 步骤4：上传成功，更新状态
      updateReferenceAndMentions(uploadFile.id, {
        status: 'done',
        prompt: uploadFile.file,
      });
    } else {
      // 上传失败，直接移除
      removeReferenceAndMentions(uploadFile.id, false);
    }
  };

  const handleUploadVideo = (file: VideoUploadFile) => {
    if (file.status === 'uploading') {
      if (getFormParams().referencePrompts?.some((prompt) => prompt.id === file.id)) {
        if (file.file) {
          updateReferenceAndMentions(file.id, { prompt: toVideoPrompt(file.file) }, false);
        }
        return;
      }

      // 如果未登录且不允许临时模式，弹出登录弹窗并拦截
      if (!userInfo && !allowTempMode) {
        openLoginDialog();
        reportEvent('login_pop_exposure', {
          refer_element: 'upload-video',
          refer_page: pathname,
        });
        return;
      }

      // 步骤1：立即生成名称
      const existedReferencePrompts = getFormParams().referencePrompts || [];
      const nextNameIdx = getNextAvailableVideoIdx(existedReferencePrompts);
      const nextVideoName = getNextVideoName(nextNameIdx, locale);

      // 步骤2：添加到参考列表和文案中
      addReferenceAndMentions({
        type: 'video',
        status: 'uploading',
        prompt: null,
        id: file.id,
        name: nextVideoName,
      });
      return;
    }

    if (file.status === 'done' && file.file) {
      // 步骤3：上传成功，更新状态
      updateReferenceAndMentions(file.id, {
        status: 'done',
        prompt: toVideoPrompt(file.file),
      });
    } else {
      // 上传失败，直接移除
      removeReferenceAndMentions(file.id, false);
    }
  };

  const handleUploadAudio = (file: AudioUploadFile) => {
    if (file.status === 'uploading') {
      if (!userInfo && !allowTempMode) {
        openLoginDialog();
        reportEvent('login_pop_exposure', {
          refer_element: 'upload-audio',
          refer_page: pathname,
        });
        return;
      }

      const existedReferencePrompts = getFormParams().referencePrompts || [];
      if (getAudioReferenceCount(existedReferencePrompts) >= MAX_AUDIO_REFERENCE_PROMPTS) {
        setShowMaxReferenceError(true);
        return;
      }

      const nextNameIdx = getNextAvailableAudioIdx(existedReferencePrompts, locale);
      const nextAudioName = getNextAudioName(nextNameIdx, locale);

      addReferenceAndMentions({
        type: 'audio',
        status: 'uploading',
        prompt: null,
        id: file.id,
        name: nextAudioName,
      });
      return;
    }

    if (file.status === 'done' && file.file) {
      updateReferenceAndMentions(file.id, {
        status: 'done',
        prompt: file.file,
      });
    } else {
      removeReferenceAndMentions(file.id, false);
    }
  };

  const handleClearPrompts = () => {
    const currentFormParams = getFormParams();
    const inspirationImageUrl = currentFormParams.inspirationImageUrl;

    // 把之前的 referencePrompts 添加到回收站
    setDeletedReferences((prev) =>
      referencePrompts.reduce((acc, prompt) => {
        // 检查是否是灵感图，如果是则保存灵感参数
        let savedInspirationParams:
          | { inspirationId?: string; inspirationFrom?: string; inspirationImageUrl?: string }
          | undefined;

        if (prompt.prompt && 'sourceImage' in prompt.prompt && prompt.prompt.sourceImage === inspirationImageUrl) {
          savedInspirationParams = {
            inspirationId: currentFormParams.inspirationId,
            inspirationFrom: currentFormParams.inspirationFrom,
            inspirationImageUrl: currentFormParams.inspirationImageUrl,
          };
        }

        acc[prompt.id] = {
          ...prompt,
          ...(savedInspirationParams && { inspirationParams: savedInspirationParams }),
        };
        return acc;
      }, prev),
    );

    // 清空灵感参数（如果有的话）
    if (
      inspirationImageUrl &&
      referencePrompts.some(
        (prompt) =>
          prompt.prompt && 'sourceImage' in prompt.prompt && prompt.prompt.sourceImage === inspirationImageUrl,
      )
    ) {
      setFormParams(taskType, {
        inspirationId: undefined,
        inspirationFrom: undefined,
        inspirationImageUrl: undefined,
      });
    }

    setTextPrompt([]);
    setReferencePrompts([]);
    onReferencePromptsChange?.();
  };

  const setPromptFromExample = (
    exampleText: string,
    exampleImages: ImagePrompt[] = [],
    exampleVideos: VideoPrompt[] = [],
    exampleAudios: SourceAudio[] = [],
  ) => {
    // 创建映射用于存储图片索引对应的ID和名称
    const imageMap = exampleImages.map((_, idx) => {
      const imageName = getNextImageName(1 + idx, locale);
      return {
        id: imageName,
        name: imageName,
      };
    });

    // 创建映射用于存储视频索引对应的ID和名称
    const videoMap = exampleVideos.map((_, idx) => {
      const videoName = getNextVideoName(1 + idx, locale);
      return {
        id: videoName,
        name: videoName,
      };
    });

    const audioMap = exampleAudios.map((_, idx) => {
      const name = getNextAudioName(idx + 1, locale);
      return { id: name, name };
    });

    // 把之前的 referencePrompts 添加到回收站（名字即将被覆盖的除外）
    const currentFormParams = getFormParams();
    const inspirationImageUrl = currentFormParams.inspirationImageUrl;

    setDeletedReferences((prev) =>
      referencePrompts.reduce((acc, prompt) => {
        const isImageConflict = prompt.type === 'image' && imageMap.some((img) => img.name === prompt.name);
        const isVideoConflict = prompt.type === 'video' && videoMap.some((vid) => vid.name === prompt.name);

        const isAudioConflict = prompt.type === 'audio' && audioMap.some((audio) => audio.name === prompt.name);
        if (!isImageConflict && !isVideoConflict && !isAudioConflict) {
          // 检查是否是灵感图，如果是则保存灵感参数
          let savedInspirationParams:
            | { inspirationId?: string; inspirationFrom?: string; inspirationImageUrl?: string }
            | undefined;

          if (prompt.prompt && 'sourceImage' in prompt.prompt && prompt.prompt.sourceImage === inspirationImageUrl) {
            savedInspirationParams = {
              inspirationId: currentFormParams.inspirationId,
              inspirationFrom: currentFormParams.inspirationFrom,
              inspirationImageUrl: currentFormParams.inspirationImageUrl,
            };
          }

          acc[prompt.id] = {
            ...prompt,
            ...(savedInspirationParams && { inspirationParams: savedInspirationParams }),
          };
        }
        return acc;
      }, prev),
    );

    // 解析文本中的 [@image{idx}] 和 [@video{idx}] 占位符，并将文本拆分为多个部分
    const parts: TextPromptWithInputItem = [];
    const regex = /\[@(image|video|audio)(\d+)\]/g;
    let lastIndex = 0;

    // 解析文本，将文本和 mention 交替添加到parts数组
    let match = regex.exec(exampleText);
    while (match !== null) {
      // 添加匹配前的文本
      if (match.index > lastIndex) {
        parts.push({
          type: 'text',
          text: exampleText.substring(lastIndex, match.index),
        });
      }

      const type = match[1] as 'image' | 'video' | 'audio';
      const idx = Number.parseInt(match[2], 10) - 1;

      // 确保索引有效并添加提及
      if (type === 'image' && idx >= 0 && idx < exampleImages.length) {
        parts.push({
          type: 'mention',
          mention: {
            id: imageMap[idx].id,
            name: imageMap[idx].name,
            color: MENTION_COLOR_MAP.image,
          },
        });
      } else if (type === 'video' && idx >= 0 && idx < exampleVideos.length) {
        parts.push({
          type: 'mention',
          mention: {
            id: videoMap[idx].id,
            name: videoMap[idx].name,
            color: MENTION_COLOR_MAP.video,
          },
        });
      }

      if (type === 'audio' && idx >= 0 && idx < exampleAudios.length) {
        parts.push({ type: 'mention', mention: { ...audioMap[idx], color: MENTION_COLOR_MAP.audio } });
      }

      lastIndex = match.index + match[0].length;
      match = regex.exec(exampleText);
    }

    // 添加最后一部分文本
    if (lastIndex < exampleText.length) {
      parts.push({
        type: 'text',
        text: exampleText.substring(lastIndex),
      });
    }

    setTextPrompt(parts);

    // 清空灵感参数（如果有的话）
    if (
      inspirationImageUrl &&
      referencePrompts.some(
        (prompt) =>
          prompt.prompt && 'sourceImage' in prompt.prompt && prompt.prompt.sourceImage === inspirationImageUrl,
      )
    ) {
      setFormParams(taskType, {
        inspirationId: undefined,
        inspirationFrom: undefined,
        inspirationImageUrl: undefined,
      });
    }

    // 设置参考提示
    const newImageReferences: ReferencePrompt[] = exampleImages.map((image, idx) => ({
      type: 'image',
      status: 'done',
      prompt: image,
      id: imageMap[idx].id,
      name: imageMap[idx].name,
    }));

    const newVideoReferences: ReferencePrompt[] = exampleVideos.map((video, idx) => ({
      type: 'video',
      status: 'done',
      prompt: video,
      id: videoMap[idx].id,
      name: videoMap[idx].name,
    }));

    const newAudioReferences: ReferencePrompt[] = exampleAudios.map((audio, idx) => ({
      type: 'audio',
      status: 'done',
      prompt: audio,
      ...audioMap[idx],
    }));
    setReferencePrompts([...newVideoReferences, ...newImageReferences, ...newAudioReferences]);
    onReferencePromptsChange?.();
  };

  const recoverReferenceFromTrash = (id: string) => {
    const deletedReference = deletedReferences[id];
    if (!deletedReference) return false;

    // 恢复灵感参数（如果有的话）
    if (deletedReference.inspirationParams) {
      setFormParams(taskType, {
        inspirationId: deletedReference.inspirationParams.inspirationId,
        inspirationFrom: deletedReference.inspirationParams.inspirationFrom,
        inspirationImageUrl: deletedReference.inspirationParams.inspirationImageUrl,
      });
    }

    const { inspirationParams: _inspirationParams, associatedReferences, ...savedReference } = deletedReference;
    let originalReference = savedReference as ReferencePrompt;
    const currentForm = getFormParams();
    const references = currentForm.referencePrompts ?? [];
    const voiceId =
      originalReference.type === 'element' && originalReference.useElementVoice
        ? originalReference.prompt?.voice?.id
        : undefined;
    const currentAudio = findVoiceReference(references, voiceId);
    const savedAudio = findVoiceReference(associatedReferences ?? Object.values(deletedReferences), voiceId);
    let voiceAudio = currentAudio ?? savedAudio;
    if (!currentAudio && voiceAudio && getAudioReferenceCount(references) >= MAX_AUDIO_REFERENCE_PROMPTS) {
      voiceAudio = undefined;
      setShowMaxReferenceError(true);
    }
    if (voiceId && !voiceAudio && originalReference.type === 'element') {
      originalReference = { ...originalReference, useElementVoice: false };
    }
    const restoreAudioMention = (items: TextPromptWithInputItem) =>
      voiceAudio && !items.some((item) => item.type === 'mention' && item.mention.id === voiceAudio.id)
        ? [...items, ...getDefaultMentionItems(voiceAudio)]
        : items;
    setFormParams(taskType, {
      referencePrompts: [...references, originalReference, ...(!currentAudio && voiceAudio ? [voiceAudio] : [])],
      ...(isProMode && canUseProMode
        ? {
            recaptionPrompt: {
              ...(currentForm.recaptionPrompt ?? getDefaultRecaptionPrompt(transProMode)),
              subject: restoreAudioMention(currentForm.recaptionPrompt?.subject ?? []),
            },
          }
        : { textPromptWithReference: restoreAudioMention(currentForm.textPromptWithReference ?? []) }),
    });
    onReferencePromptsChange?.();

    // 从回收站中移除（因为恢复了，不需要 revoke blob URL）
    removeReferenceFromTrash(id, false);

    // setFormParams normalizes names; return those identities so the editor saves the same content.
    const restoredReferences = getFormParams().referencePrompts ?? [];
    const restoredReference = restoredReferences.find((reference) => reference.id === id);
    const restoredAudio = findVoiceReference(restoredReferences, voiceId);
    return {
      id,
      label: restoredReference?.name ?? originalReference.name,
      additionalMentions: restoredAudio
        ? [{ id: restoredAudio.id, label: restoredAudio.name, mediaType: 'audio' as const }]
        : [],
    };
  };

  const handleBeforeAddMention: BeforeAddMention = (mentionItem) => {
    // 如果文本长度超出，显示错误并拒绝添加
    const contentLength = getTextPromptLength(getFormParams().textPromptWithReference || []);

    if (contentLength + mentionItem.label.length + 1 >= promptMaxLength) {
      toast({
        type: 'warning',
        message: t('create.createForm.error.invalidLengthWithMention', { limit: promptMaxLength }),
      });

      return false;
    }

    const existedReferencePrompts = getFormParams().referencePrompts || [];

    // 如果已经存在，可以直接 @ 成功，不需要添加到 referencePrompts
    if (existedReferencePrompts.some((ref) => ref.id === mentionItem.id)) {
      return true;
    }

    const deletedReference = deletedReferences[mentionItem.id];
    const currentAudio =
      deletedReference?.type === 'audio'
        ? findVoiceReference(existedReferencePrompts, deletedReference.prompt?.originElementVoiceId)
        : undefined;
    if (currentAudio) {
      // Reuse consumes no slot. Keep the deleted identity recoverable for later pastes.
      return { id: currentAudio.id, label: currentAudio.name };
    }

    // 如果已经达到最大参考数量限制，显示错误并拒绝添加
    const modelVersion = getFormParams().modelVersion;

    const isAudioMention = mentionItem.type === 'audio' || mentionItem.mediaType === 'audio';

    if (isAudioMention) {
      if (getAudioReferenceCount(existedReferencePrompts) >= MAX_AUDIO_REFERENCE_PROMPTS) {
        setShowMaxReferenceError(true);
        return false;
      }
    } else if (mentionItem.element?.modality === 'video') {
      const maxVideoCount = getMaxVideoCount(modelVersion, existedReferencePrompts);
      if (getVideoAndVideoElementCount(existedReferencePrompts) >= maxVideoCount) {
        // 对于视频数量限制，暂时共用 showMaxReferenceError 或给出特定提示
        toast({
          type: 'warning',
          message: t('create.createForm.error.invalidVideoCount', { count: maxVideoCount }),
        });
        return false;
      }
    } else {
      const maxImageAndElementCount = getMaxImageAndElementCount(modelVersion);
      if (getImageAndElementCount(existedReferencePrompts) >= maxImageAndElementCount) {
        setShowMaxReferenceError(true);
        return false;
      }
    }

    // Prefer the saved reference (including voice bindings) when a suggestion restores a deleted subject.
    if (deletedReference || !mentionItem.element) {
      return recoverReferenceFromTrash(mentionItem.id);
    }

    // 添加到 referencePrompts 末尾
    setReferencePrompts((prev) => [
      ...prev,
      {
        type: 'element' as const,
        status: 'done' as const,
        prompt: mentionItem.element || null,
        id: mentionItem.id,
        name: mentionItem.label,
        version: mentionItem.element?.version || '',
      },
    ]);
    onReferencePromptsChange?.();

    return true;
  };

  // 替换图片
  const handleReplaceImage = (index: number, newImage: ImageUploadFile): UploadImageType | null => {
    const referencePrompts = getFormParams().referencePrompts || [];
    const targetPrompt = referencePrompts[index];
    if (targetPrompt?.type !== 'image') return null; // 目前只替换图片

    if (newImage.status === 'uploading') {
      //先触发loading，但是暂不处理
      return null;
    }

    if (newImage.status === 'done' && newImage.file) {
      // loading结束，上传成功，更新图片数据
      updateReferenceAndMentions(targetPrompt.id, {
        status: 'done',
        prompt: newImage.file,
      });
      return newImage.file;
    }
    if (newImage.status === 'error') {
      toast({
        type: 'error',
        message: t('create.createForm.upload.error.invalidImage'),
      });
    }
    return null;
  };

  const handleSwapReferences = (sourceIdx: number, targetIdx: number) => {
    const currentReferencePrompts = getFormParams().referencePrompts || [];

    // 验证索引有效性
    if (
      sourceIdx < 0 ||
      sourceIdx >= currentReferencePrompts.length ||
      targetIdx < 0 ||
      targetIdx >= currentReferencePrompts.length ||
      sourceIdx === targetIdx
    ) {
      return;
    }

    // 交换数组中的两个元素
    setReferencePrompts((prev) => {
      const newArray = [...prev];
      [newArray[sourceIdx], newArray[targetIdx]] = [newArray[targetIdx], newArray[sourceIdx]];
      return newArray;
    });

    // 触发回调通知变更
    onReferencePromptsChange?.();
  };

  // 处理抽帧回填图片
  const handleBackfillImagePrompt = ({ imagePrompt, nextId }: { imagePrompt: UploadImageType; nextId?: string }) => {
    const prompts = getFormParams().referencePrompts || [];
    const imgOrElementPrompts = prompts.filter((prompt) => prompt.type === 'image' || prompt.type === 'element');
    const nextNameIdx = getNextAvailableImageIdx(prompts, locale);
    const nextImageId = nextId ?? getBackfillImageId(nextNameIdx);
    const nextImageName = getNextImageName(nextNameIdx, locale); // 生成下一个图片名称
    const maxImageAndElementCount = getMaxImageAndElementCount(getFormParams().modelVersion);
    // 生成新的参考图
    const newImageReference: ReferencePrompt = {
      type: 'image',
      status: 'done',
      prompt: imagePrompt,
      id: nextImageId,
      name: nextImageName,
    };
    if (imgOrElementPrompts.length < maxImageAndElementCount) {
      if (!checkAndToastTextPromptLength(getDefaultMentionItems(newImageReference))) return; // 检查文本长度
      removeImageInTrash(nextImageName); // 检查回收站冲突并清理
      if (!addReferenceAndMentions(newImageReference)) return; // 添加到 referencePrompts 末尾
    } else {
      // 参考项已满，替换最后一个
      const lastPrompt = imgOrElementPrompts[maxImageAndElementCount - 1];
      removeReferenceAndMentions(lastPrompt.id, false);
      // 替换时不需要长度检查，因为只是换个图片，不会增加总长度
      removeImageInTrash(nextImageName);
      if (!addReferenceAndMentions(newImageReference)) return;
    }

    return { id: nextImageId };
  };

  return {
    textPrompt,
    updateTextPrompt: setTextPrompt,
    referencePrompts,
    addReferenceAndMentions,
    removeReferenceAndMentions,
    updateReferenceAndMentions,
    removeReferenceFromTrash,
    handleUploadImage,
    handleUploadVideo,
    handleUploadAudio,
    handleClearPrompts,
    setPromptFromExample,
    handleBeforeAddMention,
    handleBeforeDeleteMention,
    showMaxReferenceError,
    resetMaxReferenceError: () => setShowMaxReferenceError(false),
    handleReplaceImage,
    handleSwapReferences,
    handleBackfillImagePrompt,
  };
}
